import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ActivityMap, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { FlyoutMenu } from "@com.mgmtp.a12.widgets/widgets-core/lib/menu/main/flyout-menu.view";
import { MenuItem } from "@com.mgmtp.a12.widgets/widgets-core/lib/menu/main/menu.api";
import { SlidingMenu } from "@com.mgmtp.a12.widgets/widgets-core/lib/menu/main/sliding-menu.view";
import { ApplicationActions, FrameViews, LocaleSelectors } from "@com.mgmtp.a12.client/client-core";

import { getFVManifests } from "../app/slices/fachverfahren/selectors";
import { Item, mapAppmodelsToItems } from "../utilities/mainMenuHelpers";

import MainMenuProps = FrameViews.MainMenuProps;

const mainNavigationAriaLabel = "Hauptnavigation";

export const CustomMainMenu: React.ComponentType<React.PropsWithChildren<MainMenuProps>> = function CustomMainMenu(
	props
) {
	const { mobileMode, onMenuItemClick } = props;

	const topLevelActivities = useSelector(ActivitySelectors.topLevelActivities());
	const locale = useSelector(LocaleSelectors.locale());
	const appmodel = useSelector(ModelSelectors.applicationModel());
	const fachverfahrenManifests = useSelector(getFVManifests);

	const dispatch = useDispatch();

	const handleClick = useCallback(
		(item: Item) => {
			if (!item.activityDescriptor) {
				return;
			}

			onMenuItemClick();
			dispatch(ApplicationActions.startMainActivityRequested({ descriptor: item.activityDescriptor }));

			//set Focus to the Main Container, because this is the element, which contains the content, where the next focussable element is
			const mainContainerDiv = document.querySelector(".content .main__container");
			if (mainContainerDiv instanceof HTMLElement) {
				mainContainerDiv.focus();
			}
		},
		[dispatch, onMenuItemClick]
	);

	/**
	 * Function to convert an item to a MenuItem
	 * @param item
	 * @returns
	 */
	let idCounter = 0;
	const mapToWidgetItem = useCallback(
		(item: Item): MenuItem => {
			return {
				id: (idCounter++).toString(),
				label: item.label.text,
				onClick: () => handleClick(item),
				children: item.children ? item.children.map(mapToWidgetItem) : undefined,
				selected: item.active
			};
		},
		[handleClick, idCounter]
	);

	const items = useMemo(() => {
		const topLevelActivitiesDescriptors = ActivityMap.toOrderedList(topLevelActivities).map(a => a.descriptor);

		return mapAppmodelsToItems(
			appmodel,
			topLevelActivitiesDescriptors,
			locale,
			fachverfahrenManifests.flatMap(manifest => manifest.moduleIds)
		).map(mapToWidgetItem);
	}, [appmodel, locale, fachverfahrenManifests, mapToWidgetItem, topLevelActivities]);

	return mobileMode ? (
		<CustomSlidingMenu {...props} items={items} />
	) : (
		<FlyoutMenu items={items} type="horizontal" mainContainerLabel={mainNavigationAriaLabel} />
	);
};

export const CustomSlidingMenu: React.ComponentType<React.PropsWithChildren<MainMenuProps> & { items: MenuItem[] }> =
	function CustomSlidingMenu(props) {
		const { expanded, triggerElement, items } = props;

		const slidingMenuWrapper = React.useRef<HTMLElement | null>(null);

		const onTabOut = useCallback<(event: React.KeyboardEvent<HTMLElement>) => void>(
			e => {
				e.preventDefault();
				if (triggerElement) {
					triggerElement.current?.focus();
				}
			},
			[triggerElement]
		);

		React.useEffect(() => {
			if (expanded) {
				setTimeout(() => slidingMenuWrapper.current?.focus(), 300);
			}
		}, [expanded]);

		return (
			<SlidingMenu.MainWrapper expanded={expanded}>
				<SlidingMenu
					items={items}
					onTabOut={onTabOut}
					wrapperRef={ref => {
						slidingMenuWrapper.current = ref;
					}}
					mainContainerLabel={mainNavigationAriaLabel}
				/>
			</SlidingMenu.MainWrapper>
		);
	};
